import { css, isRequestError, styled, useActions, useImmer } from '@topwrite/common';
import { find } from 'lodash';
import nodePath from 'path';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, Overlay, Popover } from 'react-bootstrap';
import { BsChevronDown, BsFileText, BsTools } from 'react-icons/bs';
import { FaICursor } from 'react-icons/fa';
import { GrAttachment } from 'react-icons/gr';
import { IoMdClose, IoMdSend, IoMdSquare } from 'react-icons/io';
import TextareaAutosize from 'react-textarea-autosize';
import Tooltip from '../../components/tooltip';
import { ReactComponent as Gudelines } from '../../images/guidelines.svg';
import { ReactComponent as Thinking } from '../../images/thinking.svg';
import useFormatMessage from '../../lib/use-format-message';
import useOverlayState from '../../lib/use-overlay-state';
import ChangedFiles from './changed-files';
import { useContext } from './context';
import FileItem from './file-item';
import TodoList from './todo-list';
import ToolsConfigModal from './tools-config-modal';

export const SUPPORT_FILE_TYPES = ['.txt', '.md', '.pdf', '.docx', '.xlsx', '.pptx', '.png', '.jpg'];

interface UploadedFile {
    file: File;
    path?: string;
    error?: string;
}

export default function InputBox() {
    const { setCurrent, setExtra } = useActions('workspace');
    const { send, stop, upload, loading, filename, config, setConfig, models, setFilename } = useContext();
    const [focused, setFocused] = useState(false);
    const [query, setQuery] = useState('');
    const { show: showToolsModal, state: toolsModalState } = useOverlayState();
    const t = useFormatMessage();
    const textarea = useRef<HTMLTextAreaElement>(null);
    const fileRef = useRef<HTMLInputElement>(null);
    const [uploadedFiles, setUploadedFiles] = useImmer<UploadedFile[]>([]);

    const handleFileChange = useCallback(({ target }: ChangeEvent<HTMLInputElement>) => {
        if (target.files) {
            const selectedFiles = Array.from(target.files).filter((file) => {
                //检查后缀
                if (!SUPPORT_FILE_TYPES.includes(nodePath.extname(file.name))) {
                    return false;
                }
                //检查文件大小
                if (file.size > 1024 * 1024 * 20) {
                    return false;
                }
                return file;
            }).slice(0, 6 - uploadedFiles.length);

            setUploadedFiles(draft => {
                draft.push(...selectedFiles.map((file) => {
                    return {
                        file
                    };
                }));
            });

            //上传文件
            selectedFiles.forEach(async (file) => {
                try {
                    const result = await upload(file);

                    setUploadedFiles(draft => {
                        const f = draft.find(f => f.file === file);
                        if (f) {
                            f.path = result.path;
                        }
                    });
                } catch (e) {
                    const message = isRequestError(e) ? e.message : 'unknown error';
                    setUploadedFiles(draft => {
                        const f = draft.find(f => f.file === file);
                        if (f) {
                            f.error = message;
                        }
                    });
                }
            });
        }

        target.value = '';
    }, [uploadedFiles]);

    const files = uploadedFiles.flatMap(({ file, path }) => {
        if (path) {
            return [{
                name: file.name,
                path
            }];
        }
        return [];
    });

    const sendMessage = useCallback(async () => {
        if (query) {
            setQuery('');
            setUploadedFiles([]);
            await send(query, files);
            requestAnimationFrame(() => {
                textarea.current?.focus();
            });
        }
    }, [query, files, send]);

    const selectedModel = find(models, { name: config.model });

    useEffect(() => {
        if (!selectedModel) {
            requestAnimationFrame(() => {
                setConfig(draft => {
                    draft.model = models[0].name;
                })
            });
        }
    }, []);

    return <>
        <Container>
            <TodoList />
            <ChangedFiles />
            <InputArea $focused={focused}>
                <ContextBar>
                    <Tooltip placement={'top'} tooltip={'工具配置'}>
                        <Item onClick={() => {
                            showToolsModal();
                        }}>
                            <BsTools />
                        </Item>
                    </Tooltip>
                    <Tooltip placement={'top'} tooltip={'自定义指令'}>
                        <Item onClick={() => {
                            setCurrent('.topwrite/rules.md');
                            setExtra(null);
                        }}>
                            <Gudelines />
                        </Item>
                    </Tooltip>
                    <Cursor />
                    {filename && <TextItem $active={true}>
                        <BsFileText className={'me-1'} />
                        <span>{filename}</span>
                        <CloseIcon onClick={(e) => {
                            e.stopPropagation();
                            setFilename(null);
                        }}>
                            <IoMdClose />
                        </CloseIcon>
                    </TextItem>}
                </ContextBar>
                {uploadedFiles.length > 0 && <FileArea>
                    {uploadedFiles.map(({ file, error, path }, index) => {
                        const loading = error === undefined && path === undefined;
                        return <StyledFileItem
                            key={index}
                            name={file.name}
                            error={error}
                            loading={loading}
                            onRemove={() => {
                                setUploadedFiles(draft => {
                                    const index = draft.findIndex(f => f.file === file);
                                    if (index !== -1) draft.splice(index, 1);
                                });
                            }}
                        />;
                    })}
                </FileArea>}
                <div className={'d-grid'}>
                    <TextareaAutosize
                        className={'px-2 py-1'}
                        ref={textarea}
                        placeholder={t('assistant.input_placeholder')}
                        minRows={3}
                        maxRows={8}
                        disabled={loading}
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (loading) {
                                    stop();
                                } else {
                                    sendMessage();
                                }
                            }
                        }}
                    />
                </div>
                <Toolbar>
                    <ToolbarLeft>
                        {selectedModel && <Dropdown>
                            <Dropdown.Toggle as={ToolButton}>
                                {selectedModel.label} <BsChevronDown className={'ms-1'} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {models.map(model =>
                                    <Dropdown.Item key={model.name} onClick={() => setConfig(draft => {
                                        draft.model = model.name;
                                    })}>
                                        {model.label}
                                    </Dropdown.Item>)}
                            </Dropdown.Menu>
                        </Dropdown>}
                        <Tooltip placement={'top'} tooltip={'深度思考'}>
                            <ThinkingButton $active={config.thinking} onClick={() => setConfig(draft => {
                                draft.thinking = !draft.thinking;
                            })}>
                                <Thinking />
                            </ThinkingButton>
                        </Tooltip>
                    </ToolbarLeft>
                    <ToolbarRight>
                        <Tooltip placement={'top'} tooltip={'上传附件'}>
                            <ToolButton onClick={() => fileRef.current?.click()} disabled={uploadedFiles.length >= 6}>
                                <input onChange={handleFileChange} multiple accept={SUPPORT_FILE_TYPES.join(',')}
                                       ref={fileRef} type='file' hidden />
                                <GrAttachment />
                            </ToolButton>
                        </Tooltip>
                        <InputButton
                            disabled={!loading && !query}
                            $loading={loading}
                            onClick={loading ? stop : sendMessage}
                        >
                            {loading ? <IoMdSquare /> : <IoMdSend />}
                        </InputButton>
                    </ToolbarRight>
                </Toolbar>
            </InputArea>
        </Container>
        <ToolsConfigModal {...toolsModalState} />
    </>;
}

const Cursor = () => {
    const { range } = useContext();
    const ref = useRef(null);
    const { show, hide, state } = useOverlayState();

    useEffect(() => {
        hide();
    }, [range]);

    return <>
        <CursorItem ref={ref} $active={!!range} onClick={() => show()}>
            <FaICursor />
        </CursorItem>
        {range && <Overlay
            {...state}
            target={ref}
            placement='top'
            rootClose
        >
            <SelectedText>
                <Popover.Body>
                    {range.text}
                </Popover.Body>
            </SelectedText>
        </Overlay>}
    </>;
};

const SelectedText = styled(Popover)`
    --bs-popover-max-width: 30rem;
    --bs-popover-border-width: 0px;

    .popover-body {
        max-height: 10rem;
        overflow: auto;
        white-space: pre-wrap;
    }
`;

const Container = styled.div`
    background: var(--ttw-background);
    padding: 1rem;
`;

const InputArea = styled.div<{ $focused?: boolean }>`
    display: flex;
    flex-direction: column;
    border: var(--bs-border-width) var(--bs-border-style) ${props => props.$focused ? 'var(--bs-primary)' : 'var(--bs-border-color)'};
    border-radius: var(--bs-border-radius-lg);

    textarea {
        border: none;
        outline: none;
        resize: none;
        background: transparent;
        color: var(--ttw-color);

        &::placeholder {
            color: var(--bs-secondary);
        }
    }
`;

const ContextBar = styled.div`
    display: flex;
    align-items: center;
    gap: .25rem;
    background: var(--ttw-foreground);
    padding: 0.25rem 0.5rem;
    border-top-left-radius: var(--bs-border-radius-lg);
    border-top-right-radius: var(--bs-border-radius-lg);
    flex-wrap: nowrap;
`;

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    padding: 0.5rem 0.5rem;
    border-bottom-left-radius: var(--bs-border-radius-lg);
    border-bottom-right-radius: var(--bs-border-radius-lg);
`;

const ToolbarLeft = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
`;

const ToolbarRight = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
    margin-left: auto;
`;

const Item = styled.div<{ $active?: boolean }>`
    font-size: 12px;
    color: var(--bs-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 .5rem;
    height: 22px;
    border-radius: 0.25rem;
    cursor: pointer;
    flex-shrink: 0;
    ${props => props.$active && css`
        background: var(--ttw-box-hover-background);
    `};

    &:hover {
        background: var(--ttw-box-hover-background);
    }
`;

const TextItem = styled(Item)`
    flex: 0 1 auto;
    min-width: 0;
    justify-content: start;
    display: flex;
    gap: .25rem;

    svg {
        flex-shrink: 0;
    }

    span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;

const CloseIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 0.125rem;
    margin-left: auto;
    opacity: 0.6;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 1;
        background: rgba(var(--bs-danger-rgb), 0.1);
        color: var(--bs-danger);
    }
`;

const CursorItem = styled.div<{ $active?: boolean }>`
    font-size: 12px;
    color: var(--bs-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 .5rem;
    height: 22px;
    border-radius: 0.25rem;
    cursor: pointer;
    flex-shrink: 0;
    ${props => props.$active && css`
        background: var(--bs-primary-100);
        color: var(--bs-primary);
    `};
`;

const StyledFileItem = styled(FileItem)`
    width: calc((100% - .5rem) / 2);
`;

const FileArea = styled.div`
    display: flex;
    flex-wrap: wrap;
    padding: .5rem;
    gap: .5rem;
    border-bottom: 1px solid var(--bs-border-color);
`;

const ToolButton = styled.button`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    border-radius: .25rem;
    outline: none;
    border: none;
    background: transparent;
    color: var(--bs-secondary);
    font-size: 12px;

    svg {
        flex-shrink: 0;

        path {
            stroke: currentColor;
        }
    }

    &:hover {
        background: var(--ttw-box-hover-background);
        color: var(--ttw-color);
    }
`;

const ThinkingButton = styled(ToolButton)<{ $active?: boolean }>`
    ${props => props.$active && css`
        background: var(--bs-primary-100);
        color: var(--bs-primary);

        &:hover {
            background: var(--bs-primary-100);
            color: var(--bs-primary);
        }
    `};
`;

const InputButton = styled.button<{ $loading: boolean }>`
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: .25rem;
    outline: none;
    border: none;
    background: var(--bs-primary);
    color: var(--bs-white);
    position: relative;
    font-size: 12px;

    svg {
        flex-shrink: 0;
    }

    ${props => props.$loading && css`
        color: var(--bs-danger);
        background: rgba(var(--bs-danger-rgb), .15);
    `};

    &:disabled {
        background: var(--ttw-button-disabled-background);
        color: var(--ttw-button-disabled-color);
        cursor: not-allowed;
    }

    &:hover&:not(:disabled) {
        opacity: 0.9;
    }

`;
